import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Lock, Loader2, Upload, FileText, X, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useEntitlement } from "@/context/AuthContext";
import { uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload, limitHint } from "@/lib/uploadLimits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const VALID_EXT = ["pdf", "ppt", "pptx"];

export function PitchDeckButton({ capitalId, capitalName }) {
  const navigate = useNavigate();
  const allowed = useEntitlement("capital.pitchdeck.submit");
  const [mode, setMode] = useState(null); // upgrade | no-business | form
  const [businesses, setBusinesses] = useState([]);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (!allowed) { setMode("upgrade"); return; }
    try {
      const [me, mine] = await Promise.all([
        api.get("/auth/me").then((r) => r.data).catch(() => ({})),
        api.get("/orgs/business/mine").then((r) => r.data).catch(() => []),
      ]);
      const eligible = (mine || []).filter((b) => b.published && (b.is_owner || b.my_role === "admin"));
      if (eligible.length === 0) { setMode("no-business"); return; }
      setBusinesses(eligible);
      setForm({
        first_name: me.first_name || "", last_name: me.last_name || "", email: me.email || "",
        phone_country_code: me.phone_country_code || "+1", phone: me.phone || "",
        from_business_id: eligible.length === 1 ? eligible[0].id : "", message: "",
      });
      setFile(null);
      setMode("form");
    } catch {
      toast.error("Could not start your pitch deck submission.");
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!VALID_EXT.includes(ext)) { toast.error("Pitch deck must be a PDF, PPT, or PPTX file."); return; }
    const chk = validateUpload(f, "pitchdeck");
    if (!chk.ok) { toast.error(chk.message); return; }
    setFile(f);
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.from_business_id) { toast.error("Choose the business profile you're submitting from."); return; }
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("First name, last name and email are required."); return;
    }
    if (!file) { toast.error("Please attach your pitch deck."); return; }
    setBusy(true);
    try {
      const url = await uploadFile(file, "pitchdeck");
      await api.post("/inquiries", {
        type: "capital_pitchdeck",
        target_id: capitalId,
        message: form.message,
        attachment: { url, name: file.name },
        from_business_id: form.from_business_id,
        submitter: {
          first_name: form.first_name.trim(), last_name: form.last_name.trim(),
          email: form.email.trim(), phone_country_code: form.phone_country_code, phone: form.phone.trim(),
        },
      });
      toast.success("Pitch deck submitted.");
      setMode(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your pitch deck.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={open} data-testid="pitchdeck-submit-button">
        {allowed ? <Send className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />} Submit Pitch Deck
      </Button>

      <Dialog open={mode === "upgrade"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent data-testid="pitchdeck-upgrade-dialog">
          <DialogHeader>
            <DialogTitle>Upgrade to submit pitch decks</DialogTitle>
            <DialogDescription>Submitting a pitch deck to {capitalName} is a premium action. Upgrade your plan to unlock it.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMode(null)}>Maybe later</Button>
            <Button onClick={() => navigate("/pricing")} data-testid="pitchdeck-upgrade-cta">View plans</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mode === "no-business"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent data-testid="pitchdeck-no-business-dialog">
          <DialogHeader>
            <DialogTitle>A business profile is required</DialogTitle>
            <DialogDescription>Please create a business profile in order to submit a pitch deck.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMode(null)}>Cancel</Button>
            <Button onClick={() => navigate("/dashboard/businesses?new=1")} data-testid="pitchdeck-create-business-cta">
              <Building2 className="mr-2 h-4 w-4" /> Create a business profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mode === "form"} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="max-w-lg" data-testid="pitchdeck-form-dialog">
          <DialogHeader>
            <DialogTitle>Submit your pitch deck</DialogTitle>
            <DialogDescription>To {capitalName}. Your details are prefilled from your account — edit as needed.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>First name *</Label><Input value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} data-testid="pitchdeck-first-name" /></div>
                <div className="space-y-1"><Label>Last name *</Label><Input value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} data-testid="pitchdeck-last-name" /></div>
              </div>
              <div className="space-y-1"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="pitchdeck-email" /></div>
              <div className="grid grid-cols-[5.5rem_1fr] gap-2">
                <div className="space-y-1"><Label>Code</Label><Input value={form.phone_country_code} onChange={(e) => setF("phone_country_code", e.target.value)} data-testid="pitchdeck-phone-code" /></div>
                <div className="space-y-1"><Label>Phone <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="pitchdeck-phone" /></div>
              </div>
              <div className="space-y-1">
                <Label>Submitting as *</Label>
                <Select value={form.from_business_id} onValueChange={(v) => setF("from_business_id", v)}>
                  <SelectTrigger data-testid="pitchdeck-business-select"><SelectValue placeholder="Choose your business profile" /></SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Pitch deck * <span className="font-normal text-muted-foreground">(PDF, PPT, PPTX · {limitHint("pitchdeck")})</span></Label>
                {file ? (
                  <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid="pitchdeck-file-chip">
                    <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">{file.name}</span></span>
                    <button type="button" onClick={() => setFile(null)} className="text-muted-foreground hover:text-destructive" data-testid="pitchdeck-file-clear"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground hover:bg-secondary" data-testid="pitchdeck-file-drop">
                    <Upload className="h-4 w-4" /> Choose a file
                    <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={pickFile} data-testid="pitchdeck-file-input" />
                  </label>
                )}
              </div>
              <div className="space-y-1"><Label>Message <span className="font-normal text-muted-foreground">(optional)</span></Label><Textarea rows={3} value={form.message} onChange={(e) => setF("message", e.target.value)} placeholder="A short note for the investor…" data-testid="pitchdeck-message" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMode(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="pitchdeck-form-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
